import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Link as MuiLink,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Generic component to display markdown files
 * Fetches markdown content from the backend or public folder
 */
function MarkdownViewer({ 
  title, 
  fileName, 
  fallbackContent = null,
  showBackLink = true 
}) {
  const styles = useThemeStyles();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMarkdown();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [fileName]);

  const fetchMarkdown = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch from backend API using axios with text response type
      // Note: api.get uses baseURL from api.js (http://localhost:5001)
      try {
        const response = await api.get(`/api/legal/${fileName}`, {
          responseType: 'text', // Important: get raw text, not JSON
          headers: {
            'Accept': 'text/markdown, text/plain, */*'
          }
        });

        // Get the content (axios puts text in response.data when responseType is 'text')
        const content = response.data;

        // Validate that we got markdown, not HTML
        if (!content || typeof content !== 'string') {
          throw new Error('Invalid response format from server');
        }

        // Check if we got HTML (React app's index.html) instead of markdown
        const trimmedContent = content.trim();
        if (trimmedContent.startsWith('<!DOCTYPE') || trimmedContent.startsWith('<html')) {
          throw new Error(
            'Received HTML instead of markdown. This usually means the request went to the React dev server instead of the backend API. ' +
            'Please check that the backend server is running on port 5001 and that REACT_APP_API_URL is set correctly.'
          );
        }

        setContent(content);
        setLoading(false);
        return;
      } catch (apiError) {
        // Try direct fetch as fallback (in case axios has issues with text response)
        try {
          const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
          const fullUrl = `${apiBaseUrl}/api/legal/${fileName}`;

          const fetchResponse = await fetch(fullUrl, {
            method: 'GET',
            headers: {
              'Accept': 'text/markdown, text/plain, */*'
            }
          });

          if (!fetchResponse.ok) {
            throw new Error(`HTTP ${fetchResponse.status}: ${fetchResponse.statusText}`);
          }

          const content = await fetchResponse.text();

          // Validate content
          if (content.trim().startsWith('<!DOCTYPE') || content.trim().startsWith('<html')) {
            throw new Error('Received HTML instead of markdown from direct fetch');
          }

          setContent(content);
          setLoading(false);
          return;
        } catch (fetchError) {
          // If we have fallback content, use it
          if (fallbackContent) {
            setContent(fallbackContent);
            setLoading(false);
            return;
          }

          // Provide helpful error message
          const errorMessage = apiError.response?.data?.message || 
                              apiError.message || 
                              fetchError.message ||
                              'Could not load document from server. Please ensure the backend server is running.';

          throw new Error(errorMessage);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load document');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Failed to load document: {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {showBackLink && (
        <Box sx={{ mb: 2 }}>
          <MuiLink
            component={RouterLink}
            to="/settings"
            sx={{
              textDecoration: 'none',
              color: 'text.secondary',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            ← Back to Settings
          </MuiLink>
        </Box>
      )}
      
      <Paper elevation={2} sx={{ p: 4 }}>
        {title && (
          <Typography component="h2" className="pagename" sx={styles.text.pagename}>
            {title}
          </Typography>
        )}
        
        {ReactMarkdown ? (
          <Box
            sx={{
              '& h1': { mt: 4, mb: 2, fontSize: '2rem', fontWeight: 600 },
              '& h2': { mt: 3, mb: 1.5, fontSize: '1.5rem', fontWeight: 600 },
              '& h3': { mt: 2, mb: 1, fontSize: '1.25rem', fontWeight: 600 },
              '& h4': { mt: 2, mb: 1, fontSize: '1.1rem', fontWeight: 600 },
              '& h5': { mt: 1.5, mb: 0.5, fontSize: '1rem', fontWeight: 600 },
              '& h6': { mt: 1.5, mb: 0.5, fontSize: '0.9rem', fontWeight: 600 },
              '& p': { mb: 1.5, lineHeight: 1.7 },
              '& ul, & ol': { mb: 1.5, pl: 3 },
              '& li': { mb: 0.5 },
              '& strong': { fontWeight: 600 },
              '& em': { fontStyle: 'italic' },
              '& code': {
                bgcolor: 'grey.100',
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: '0.9em',
                fontFamily: 'monospace',
              },
              '& pre': {
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                mb: 2,
              },
              '& blockquote': {
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                pl: 2,
                ml: 0,
                fontStyle: 'italic',
                color: 'text.secondary',
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                mb: 2,
              },
              '& th, & td': {
                border: '1px solid',
                borderColor: 'divider',
                p: 1,
                textAlign: 'left',
              },
              '& th': {
                bgcolor: 'grey.100',
                fontWeight: 600,
              },
            }}
          >
            <ReactMarkdown remarkPlugins={remarkGfm ? [remarkGfm] : []}>
              {content}
            </ReactMarkdown>
          </Box>
        ) : (
          <Box
            component="pre"
            sx={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              fontSize: '0.95rem',
              lineHeight: 1.7,
              mb: 0,
            }}
          >
            {content}
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default MarkdownViewer;
